import React, { useState, useEffect } from "react";
import qs from 'query-string';
import * as Yup from "yup";
import InputMask from "react-input-mask";
import { useHistory } from "react-router-dom";
import { Link as RouterLink } from "react-router-dom";
import { toast } from "react-toastify";
import { Formik, Form, Field } from "formik";
import usePlans from "../../hooks/usePlans";
import {
	Button,
	CssBaseline,
	TextField,
	Grid,
	Link,
	Container,
	Typography,
	Paper,
	InputAdornment,
	IconButton,
	MenuItem,
	Select,
	InputLabel,
	FormControl,
	CircularProgress,
	useTheme,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { i18n } from "../../translate/i18n";
import { openApi } from "../../services/api";
import toastError from "../../errors/toastError";
import moment from "moment";
import { Visibility, VisibilityOff, WhatsApp } from "@material-ui/icons";

import chatImage from "../../assets/multicanal.svg"; // Mesma imagem do login
import WhatsAppIcon from "../../assets/logologinnovo.png";
import WhatsAppIconWhite from "../../assets/logoLoginWhite.png";

// --- Estilos atualizados ---
const useStyles = makeStyles((theme) => ({
	root: {
		display: "flex",
		minHeight: "100vh",
		//background: "linear-gradient(135deg, #f77304 0%, #0e8def 70%)", // Mesmo tom do login
		[theme.breakpoints.down("sm")]: {
			flexDirection: "column",
		},
	},
	leftSide: {
		flex: 1,
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
		padding: theme.spacing(2),
	},
	rightSide: {
		flex: 1,
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
		[theme.breakpoints.down("sm")]: {
			display: "none",
		},
	},
	paper: {
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		padding: theme.spacing(4),
		borderRadius: theme.spacing(2),
		backgroundColor: theme.palette.background.paper,
		boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.12)",
		width: "100%",
		maxWidth: 420,
		position: "relative",
	},
	logoContainer: {
		display: "flex",
		justifyContent: "center",
		marginBottom: theme.spacing(1),
	},
	logo: {
		height: 60,
		width: "auto",
	},
	infoMessage: {
		textAlign: "center",
		fontSize: 14,
		//color: "#b9b9b9ff",
		marginBottom: theme.spacing(3),
		fontStyle: "italic",
		textShadow: "1px 1px 3px rgba(0,0,0,0.3)",
	},
	form: {
		width: "100%",
		marginTop: theme.spacing(2),
	},
	submit: {
		margin: theme.spacing(3, 0, 2),
	},
	image: {
		maxWidth: "90%",
		height: "auto",
		borderRadius: theme.spacing(2),
		boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.16)",
	},
	link: {
		fontWeight: 500,
	},
}));

// --- Componente Logo (reutilizável) ---
const Logo = () => {
	const theme = useTheme();
	const isDarkMode = theme.palette.type === "dark";
	return (
		<img
			src={isDarkMode ? WhatsAppIconWhite : WhatsAppIcon}
			alt="Logo"
			className={useStyles().logo}
		/>
	);
};

// --- Input de Telefone ---
const PhoneInput = ({ field, form, ...props }) => {
	return (
		<InputMask
			{...field}
			mask="(99) 99999-9999"
			maskChar={null}
		>
			{(inputProps) => (
				<TextField
					{...inputProps}
					{...props}
					error={Boolean(form.touched[field.name] && form.errors[field.name])}
					helperText={form.touched[field.name] && form.errors[field.name]}
					fullWidth
					variant="outlined"
				/>
			)}
		</InputMask>
	);
};

// --- Input de Senha com visibilidade ---
const PasswordField = ({ field, form, ...props }) => {
	const [showPassword, setShowPassword] = useState(false);
	return (
		<TextField
			{...field}
			{...props}
			type={showPassword ? "text" : "password"}
			fullWidth
			variant="outlined"
			error={Boolean(form.touched[field.name] && form.errors[field.name])}
			helperText={form.touched[field.name] && form.errors[field.name]}
			InputProps={{
				endAdornment: (
					<InputAdornment position="end">
						<IconButton
							aria-label="toggle password visibility"
							onClick={() => setShowPassword((prev) => !prev)}
							edge="end"
						>
							{showPassword ? <VisibilityOff /> : <Visibility />}
						</IconButton>
					</InputAdornment>
				),
			}}
		/>
	);
};

// --- Schema de Validação ---
const UserSchema = Yup.object().shape({
	name: Yup.string()
		.min(2, "Nome precisa ter ao menos 3 caracteres!")
		.max(50, "Nome não pode conter mais que 50 caracteres!")
		.required("Campo obrigatório"),
	password: Yup.string()
		.min(5, "Senha muito curta!")
		.max(50, "Senha muito grande!"),
	email: Yup.string()
		.email("Email inválido")
		.required("Campo obrigatório")
		.matches(/^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/, "Email inválido"),
	phone: Yup.string()
		.matches(/^\(\d{2}\) \d{5}-\d{4}$/, "Telefone inválido. Exemplo: (99) 99999-9999")
		.required("Campo obrigatório"),
	planId: Yup.number().required("Selecione um plano"),
});

// --- Limpeza de telefone ---
const cleanPhone = (phone) => {
	return phone.replace(/\D/g, "");
};

// --- Componente Principal ---
const SignUp = () => {
	const classes = useStyles();
	const history = useHistory();
	const [loading, setLoading] = useState(false);
	const [plans, setPlans] = useState([]);
	const { list: listPlans } = usePlans();

	// Extrair companyId da URL
	const params = qs.parse(window.location.search);
	const companyId = params.companyId || null;

	const dueDate = moment().add(3, "day").format();

	useEffect(() => {
		const fetchPlans = async () => {
			const list = await listPlans();
			setPlans(list);
		};
		fetchPlans();
	}, [listPlans]);

	const handleSignUp = async (values) => {
		try {
			setLoading(true);
			const cleanedValues = {
				...values,
				email: values.email.toLowerCase(),
				phone: cleanPhone(values.phone),
				recurrence: "MENSAL",
				dueDate,
				status: "t",
				campaignsEnabled: true,
				...(companyId && { companyId }),
			};

			// 1. Faz o cadastro no backend (como já faz)
			await openApi.post("/companies/cadastro", cleanedValues);

			// 2. Dispara o webhook do Zapier para enviar o e-mail de boas-vindas
			await fetch("https://hook.us2.make.com/xbsptar620e1j7ue7vi67visck901ojs", {
				method: "POST",
				mode: "no-cors", // ⚠️ Importante: evita erros CORS (não precisamos da resposta)
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({
					name: values.name,
					email: values.email,
					phone: values.phone,
					planId: values.planId,
					date: new Date().toISOString(),
					// Você pode adicionar mais campos se quiser personalizar o e-mail
				}),
			});

			// 3. Mostra sucesso e redireciona
			toast.success(i18n.t("signup.toasts.success"));
			history.push("/login");

		} catch (err) {
			console.error(err);
			toastError(err);
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className={classes.root}>
			<CssBaseline />

			{/* Lado Esquerdo - Formulário */}
			<div className={classes.leftSide}>
				<Container component="main" maxWidth="xs">
					<Paper className={classes.paper} elevation={6}>
						<div className={classes.logoContainer}>
							<Logo />
						</div>

						<Typography variant="body2" className={classes.infoMessage}>
							<span role="img" aria-label="foguete">🚀</span> Aproveite 3 dias grátis para testar todos os recursos!
						</Typography>

						<Formik
							initialValues={{
								name: "",
								email: "",
								phone: "",
								password: "",
								planId: "",
							}}
							validationSchema={UserSchema}
							onSubmit={(values, actions) => {
								setTimeout(() => {
									handleSignUp(values);
									actions.setSubmitting(false);
								}, 400);
							}}
						>
							{({ isSubmitting, touched, errors }) => (
								<Form className={classes.form}>
									<Grid container spacing={2}>
										<Grid item xs={12}>
											<Field
												as={TextField}
												autoComplete="name"
												name="name"
												error={touched.name && Boolean(errors.name)}
												helperText={touched.name && errors.name}
												variant="outlined"
												fullWidth
												id="name"
												label="Nome do seu Negócio"
											/>
										</Grid>
										<Grid item xs={12}>
											<Field
												as={TextField}
												variant="outlined"
												fullWidth
												id="email"
												label={i18n.t("signup.form.email")}
												name="email"
												error={touched.email && Boolean(errors.email)}
												helperText={touched.email && errors.email}
												autoComplete="email"
												required
											/>
										</Grid>
										<Grid item xs={12}>
											<Field
												component={PhoneInput}
												name="phone"
												label="Telefone com (DDD)"
												required
											/>
										</Grid>
										<Grid item xs={12}>
											<Field
												component={PasswordField}
												name="password"
												label={i18n.t("signup.form.password")}
												required
											/>
										</Grid>
										<Grid item xs={12}>
											<FormControl variant="outlined" fullWidth required>
												<InputLabel id="plan-selection-label">Plano</InputLabel>
												<Field
													as={Select}
													labelId="plan-selection-label"
													id="plan-selection"
													name="planId"
													label="Plano"
													style={{ minWidth: 300 }}
													MenuProps={{
														PaperProps: { style: { maxHeight: 400, width: 330 } },
														anchorOrigin: { vertical: "bottom", horizontal: "left" },
														transformOrigin: { vertical: "top", horizontal: "left" },
														getContentAnchorEl: null,
													}}
												>
													{plans.map((plan) => (
														<MenuItem
															key={plan.id}
															value={plan.id}
															style={{
																display: "flex",
																flexDirection: "column",
																alignItems: "flex-start",
																padding: "10px 16px",
																//minWidth: 300,
																position: "relative",
															}}
														>
															{plan.id === 4 && ( // Ex: plano smart
																<div
																	style={{
																	position: "absolute",
																	top: 8,
																	right: 8,
																	backgroundColor: "#f77304",
																	color: "white",
																	fontSize: 11,
																	fontWeight: "bold",
																	padding: "2px 6px",
																	borderRadius: 12,
																	}}
																>
																	Mais Popular
																</div>
															)}
															<Typography
																variant="subtitle1"
																style={{
																fontWeight: 600,
																color: "#1a1a1a",
																marginBottom: 4,
																}}
															>
																{plan.name}
															</Typography>
															<Typography
																variant="body2"
																color="textSecondary"
																style={{ fontSize: 13 }}
															>
																Atendentes: <strong>{plan.users}</strong> | WhatsApp:{" "}
																<strong>{plan.connections}</strong>
															</Typography>
															<Typography
																variant="body1"
																style={{
																fontWeight: 700,
																color: "#f77304", // cor da sua marca
																marginTop: 4,
																fontSize: "1.1rem",
																}}
															>
																R$ {parseFloat(plan.value).toFixed(2).replace('.', ',')}
															</Typography>
														</MenuItem>
														))}
												</Field>
											</FormControl>
										</Grid>
									</Grid>

									<Button
										type="submit"
										fullWidth
										variant="contained"
										color="primary"
										className={classes.submit}
										disabled={isSubmitting || loading}
										startIcon={
											(isSubmitting || loading) && (
												<CircularProgress size={20} color="inherit" />
											)
										}
									>
										{isSubmitting || loading
											? i18n.t("signup.buttons.loading")
											: i18n.t("signup.buttons.submit")}
									</Button>

									<Grid container justifyContent="flex-end">
										<Grid item>
											<Link
												component={RouterLink}
												to="/login"
												variant="body2"
												className={classes.link}
											>
												{i18n.t("signup.buttons.login")}
											</Link>
										</Grid>
									</Grid>
								</Form>
							)}
						</Formik>
					</Paper>
				</Container>
			</div>

			<div className={classes.rightSide}>
				<img src={chatImage} alt="Ilustração de chat" className={classes.image} />
			</div>

			{/* Balão de Fala - Dica de Suporte */}
			<div
				style={{
					position: 'fixed',
					bottom: '90px', // Ajuste para ficar acima do botão
					right: '30px',
					backgroundColor: '#075E54', // Verde escuro do WhatsApp (brand)
					color: 'white',
					borderRadius: '18px',
					padding: '8px 16px',
					fontSize: '14px',
					fontWeight: 500,
					boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.15)',
					zIndex: 1001,
					maxWidth: '180px',
					textAlign: 'center',
					animation: 'fadeIn 0.5s ease-in 1s forwards', // Aparece após 1s
					opacity: 0,
					// Criando a "ponta" do balão
					transform: 'translateY(0)',
				}}
			>
				<span>Dúvidas? Clique aqui e fale com nosso suporte!</span>
				<div
					style={{
						position: 'absolute',
						top: '100%',
						right: '20px',
						width: '10px',
						height: '10px',
						backgroundColor: '#075E54',
						transform: 'rotate(45deg)',
						boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
					}}
				/>
			</div>

			{/* Balão de Fala - Dica de Suporte */}
			<div
				style={{
					position: "fixed",
					bottom: "94px", // Ajustado para ficar acima do botão com espaço
    				right: "10px",  // Alinha com o centro do botão WhatsApp (24px + 30px)
    				transform: "translateX(50%)", // Centraliza o balão no ponto "right: 54px"
					backgroundColor: "#075E54", // Verde escuro WhatsApp
					color: "white",
					borderRadius: "16px",
					padding: "8px 14px",
					fontSize: "13.5px",
					fontWeight: 500,
					boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.18)",
					zIndex: 1002, // Acima do botão (zIndex 1000) e do próprio WhatsApp (1001)
					maxWidth: "190px",
					textAlign: "center",
					pointerEvents: "none", // Permite que cliques passem pelo balão
					animation: "fadeInUp 0.4s ease-out 0.6s forwards",
					opacity: 0,
				}}
			>
				<span>Dúvidas? Clique aqui e fale com nosso suporte!</span>
				{/* Ponta do balão */}
				<div
					style={{
						position: "absolute",
						top: "100%",
						left: "50%",
						transform: "translateX(-50%)",
						width: "10px",
						height: "10px",
						backgroundColor: "#075E54",
						clipPath: "polygon(0 0, 100% 0, 50% 100%)", // Triângulo limpo
					}}
				/>
			</div>

			{/* Botão Flutuante do WhatsApp */}
			<a
				href="https://wa.me/5541988977954?text=Olá,%20estou%20com%20dúvida%20no%20cadastro!"
				target="_blank"
				rel="noopener noreferrer"
				style={{
					position: "fixed",
					bottom: "24px",
					right: "50px",
					backgroundColor: "#25D366",
					color: "white",
					borderRadius: "50%",
					width: "60px",
					height: "60px",
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)",
					zIndex: 1001,
					cursor: "pointer",
					border: "2px solid white",
					transition: "all 0.3s ease-in-out",
				}}
				onMouseOver={(e) => {
					e.target.style.transform = "scale(1.1)";
					e.target.style.boxShadow = "0 8px 16px rgba(0, 0, 0, 0.3)";
				}}
				onMouseOut={(e) => {
					e.target.style.transform = "scale(1)";
					e.target.style.boxShadow = "0px 4px 10px rgba(0, 0, 0, 0.2)";
				}}
			>
				<WhatsApp style={{ fontSize: 34 }} />
			</a>

			{/* Estilo da Animação (adicione no topo do return ou dentro de um <style> tag) */}
			<style>
				{`
					@keyframes fadeInUp {
						from {
						opacity: 0;
						transform: translateY(10px);
						}
						to {
						opacity: 1;
						transform: translateY(0);
						}
					}
				`}
			</style>
		</div>
	);
};

export default SignUp;